import React, { useState, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Typography,
    Tabs,
    Tab
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";


function AllAwardsTab({ awards }) {
    if (awards.length === 0) {
        return (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                No submitted awards yet.
            </Typography>
        );
    }
    return (
        <TableContainer component={Paper} elevation={0} variant="outlined">
            <Table size="small">
                <TableHead sx={{ fontSize: "1rem"}}>
                    <TableRow>
                        <TableCell sx={{fontWeight: 600}}>Judge</TableCell>
                        <TableCell sx={{fontWeight: 600}}>Entry</TableCell>
                        <TableCell sx={{fontWeight: 600}}>Dance</TableCell>
                        <TableCell sx={{fontWeight: 600}}>School</TableCell>
                        <TableCell sx={{fontWeight: 600}}>Award Name</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {awards.map((row, i) => (
                        <TableRow key={i} hover>
                            <TableCell>{row[0]}</TableCell>
                            <TableCell>{row[1]}</TableCell>
                            <TableCell>{row[2]}</TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>{row[3]}</TableCell>
                            <TableCell>{row[4]}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}


function BySchoolTab({ awards, allSchools = [] }) {
    const schoolCounts = useMemo(() => {
        const map = {};
        allSchools.forEach(s =>  map[s] = 0 );
        awards.forEach(row => {
            const school = row[3] || "Unknown School";
            map[school] = (map[school] || 0) + 1;
        });
        return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
    }, [awards, allSchools]);

    if (schoolCounts.length === 0) {
        return (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                No submitted awards yet.
            </Typography>
        );
    }

    return (
            <TableContainer component={Paper} elevation={0} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontSize: "0.9rem", fontWeight: 600 }}>School Name</TableCell>
                            <TableCell sx={{ fontSize: "0.9rem", fontWeight: 600, textAlign:"center" }}>Number of Speacial Awards Received</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {schoolCounts.map(([school,count]) => (
                            <TableRow key={school} hover>
                                <TableCell>{school}</TableCell>
                                <TableCell sx={{textAlign: "center"}}>{count}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            
    );
}

// SubmittedAwardsDialog: displays all special award nominations with
// status "Submitted" fetched from the SpecialAwards sheet tab.
//
// Props:
//   open    — whether the dialog is visible
//   onClose — callback to close the dialog
//   loading — whether data is still being fetched
//   awards  — array of sheet rows: [email, entryNum, entryName, awardName, notes, status]
function SubmittedAwardsDialog({ open, onClose, loading, awards = [], schools = [] }) {
    const [activeTab, setActiveTab] = useState(0);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.25rem" }}>
                <EmojiEventsIcon color="primary" />
                Submitted Special Awards
            </DialogTitle>

            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
            >
                <Tab label="All Awards" sx={{fontWeight: 600}} />
                <Tab label="By School" sx={{fontWeight: 600}} />
            </Tabs>

            <DialogContent dividers>
                {loading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
                        <CircularProgress />
                    </div>
                ) : activeTab === 0 ? (
                    <AllAwardsTab awards={awards} />
                ) : (
                    <BySchoolTab awards={awards} allSchools={schools} />
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

export default SubmittedAwardsDialog;